import { UserIcon } from "@heroicons/react/24/solid";
import type { FlexProps } from "@highoutput/ui-components";
import {
  AspectRatio,
  Avatar,
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Text,
} from "@highoutput/ui-components";
import MarkdownIt from "markdown-it";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import type { PostCoreDataFragment } from "~/__generated__/graphql";
import type Nullable from "~/types/nullable";
import takeFirstParagraph from "~/utils/take-first-paragraph";

const md = new MarkdownIt({
  html: true,
});

type HighlightProps = {
  data?: Nullable<PostCoreDataFragment>;
};

export default function Highlight({
  data,
  ...props
}: HighlightProps & Omit<FlexProps, "children">) {
  const [contentPreview, setContentPreview] = useState("");

  useEffect(() => {
    setContentPreview(md.render(takeFirstParagraph(data?.attributes?.content)));

    return () => setContentPreview("");
  }, [data?.attributes?.content]);

  if (!data) return null;

  return (
    <Flex
      direction={{
        base: "column",
        lg: "row",
      }}
      gap={{
        base: "16px",
        lg: "32px",
      }}
      align={{
        md: "start",
      }}
      {...props}
    >
      {data.attributes?.cover.data?.attributes?.url && (
        <AspectRatio
          ratio={16 / 9}
          flexShrink={0}
          width={{
            base: "full",
            lg: "613px",
          }}
        >
          <Image
            src={data.attributes.cover.data.attributes?.url}
            alt="Cover"
            data-testid={`posts.highlight.${data.id}.cover`}
            width={1200}
            height={600}
            priority
          />
        </AspectRatio>
      )}

      <Box>
        <Heading
          as={Link}
          href={`/blog/${data.attributes?.slug}`}
          display="block"
          variant="32/36"
          letterSpacing={{
            base: "-0.02em",
            lg: "-0.015em",
          }}
          overflow="hidden"
          title={data.attributes?.title}
          _hover={{
            textDecoration: "underline",
          }}
          fontFamily="Georgia"
          data-testid={`posts.highlight.${data.id}.title`}
          fontWeight={400}
        >
          {data.attributes?.title}
        </Heading>

        <Text
          color="rgba(13, 48, 80, 0.8)"
          variant={{
            base: "16/24",
            lg: "20/32",
          }}
          marginTop="12px"
          data-testid={`posts.highlight.${data.id}.description`}
          noOfLines={4}
          overflow="hidden"
          dangerouslySetInnerHTML={{
            __html: contentPreview,
          }}
        />

        <HStack
          marginTop={{
            base: "16px",
            lg: "24px",
          }}
          spacing={{
            base: "10px",
            lg: "12px",
          }}
        >
          <Avatar
            src={data.attributes?.author?.data?.attributes?.avatar?.data?.attributes?.url}
            name={data.attributes?.author?.data?.attributes?.name ?? ""}
            width={{
              base: "32px",
              lg: "40px",
            }}
            height={{
              base: "32px",
              lg: "40px",
            }}
            icon={<Icon as={UserIcon} width="24px" height="24px" />}
            bgColor="rgba(13,48,80,0.6)"
          />

          <Text
            variant="14/20"
            fontWeight="medium"
            data-testid={`posts.highlight.${data.id}.author`}
          >
            {data.attributes?.author?.data?.attributes?.name ?? "unknown"}
          </Text>
        </HStack>
      </Box>
    </Flex>
  );
}
